const PS = require("./util/ParserState");
const Helpers = require("./util/ParserHelpers");
const ColorUtil = require("../util/ColorUtil");
const CanvasImage = require("../util/CanvasImage");
const CanvasUtil = require("../util/CanvasUtil");
const Font = require("../util/Font");
const RC = require("./util/RenderController");
const LRUCache = require("../../../shared/LRUCache");
//Requiring _ so problem enterers can use it in their problems.
const _ = require("lodash"); //eslint-disable-line no-unused-vars

let diagramHelpers = {};
const injectDependencies = function(newDiagramHelpers) {
	diagramHelpers = newDiagramHelpers;
};

// Note: in addition to the diagram cache, the CanvasMath caching keeps the
// measures around, including the canvas objects. That means this cache can't
// reset its canvases when they're cleared, since something else might still
// reference them.
const diagramCache = new LRUCache(10);

diagramCache.onDestroy(diagram => {
	CanvasUtil.freeCanvasMemory(diagram.diagramCanvas, {
		source: "Diagram",
	});
});

// Draw using javascript code. ps is the parser state containing only the
// javascript code to be drawn.
// Returns a singleton array with a node of type blockDiagram on success, or
// false if something went wrong. Errors will be in the parser state.
const parseDiagram = function(ps) {
	ps = PS.convert(ps);
	const optArg = Helpers.findOptionalArgument(ps);
	const code = ps.getRest();
	//Push errors for diagrams using some ES6 or newer features that are easy to check for
	const notAllowed = code.match(/(let )|(const )|(var \[)|(var {)|(=>)|(,\))/g);
	if (notAllowed) {
		ps.pushError(
			"Newer javascript features are not allowed in diagrams. Diagram code contains " +
				notAllowed.map(s => `"${s}"`).join(" and ") +
				"."
		);
	}
	const clean = cleanDiagramCode(code);
	return [
		{
			type: "blockDiagram",
			code: clean.code,
			preambleLines: clean.preambleLines,
			specialType: optArg && optArg.text,
		},
	];
};

// Draw using javascript code. rc is the renderer controller.
const drawDiagram = function(rc, fontInfo, props, node) {
	// Check the cache first before drawing.
	const dpr = CanvasUtil.getDPR();
	const cacheKey = makeCacheKey(
		node.code,
		node.specialType,
		fontInfo,
		props,
		dpr
	);
	if (diagramCache.has(cacheKey)) {
		const cachedValue = diagramCache.get(cacheKey);
		rc.mergeErrors(cachedValue.errors);
		return cachedValue;
	}

	const defaultWidth = 100;
	const defaultHeight = 100;

	// Create new canvas to draw diagram on.
	const diagramCanvas = CanvasUtil.createCanvas(defaultWidth, defaultHeight, {
		source: "Diagram",
	});
	CanvasUtil.setCanvasDPI(diagramCanvas);
	const ctx = CanvasUtil.getShortvas(diagramCanvas);
	let hasImages = false;

	const pushedErrors = rc.trackErrors(() => {
		const getCanvasSize = () => {
			return {width: ctx.getWidth(), height: ctx.getHeight()};
		};
		const setCanvasSize = (newW, newH) => {
			CanvasUtil.setWidth(diagramCanvas, newW);
			CanvasUtil.setHeight(diagramCanvas, newH);
		};
		const isRuler =
			node.specialType === "forRuler" || node.specialType === "forFracRuler";
		if (isRuler && !props.rulerFontSize) {
			rc.pushError("Batex has ruler diagram but rulerFontSize not provided.");
		}
		setupDiagramContext(
			ctx,
			fontInfo,
			props,
			node.specialType,
			getCanvasSize,
			setCanvasSize
		);

		let errors;
		let havePushedSyntaxErrors = false;

		hasImages = CanvasImage.withLoadingForTemp(rc.context, ctx, () => {
			// Run the diagram code and record errors. If we record any, use
			// havePushedSyntaxErrors to prevent the same errors being pushed later.
			errors = evalDiagramCode(ctx, node, havePushedSyntaxErrors);
			havePushedSyntaxErrors = havePushedSyntaxErrors || !!errors.length;
		});
		errors.forEach(m => rc.pushError(m));
		// Add all errors from the diagram's Render Controller, to catch errors in
		// Batex in the diagram.
		ctx.__diagramRc.errors.forEach(e => {
			rc.pushError(`Diagram code contains a Batex error: ${e.message}`);
		});
	});

	const canvasWidth = ctx.getWidth() || defaultWidth;
	const canvasHeight = ctx.getHeight() || defaultHeight;

	// Put together data returned in measure call.
	const diagram = {
		diagramCanvas,
		diagramHeight: canvasHeight,
		diagramWidth: canvasWidth,
		blanks: ctx.__diagramRc.blanks,
		errors: pushedErrors,
	};

	// Remove all special diagram props from the canvas context so they won't be
	// around if it's used again for a diagram.
	delete ctx.__diagramRc;

	// If there are images to load, we need to redraw the diagram once they load,
	// and any caching will mess that up. So not only do we skip the diagram
	// cache, we tell CanvasMath to skip its cache is well. Conversely, if no
	// images have to load, we store what we just drew in the diagram cache.
	if (hasImages) {
		rc.skipCacheThisRun();
	} else {
		diagramCache.store(cacheKey, diagram);
	}

	return diagram;
};

// Context here may be a normal one, not necessarily a Shortvas one.
const drawDiagramForTests = function(ctx, data) {
	const {fontInfo, props, node, getCanvasSize, setCanvasSize} = data;
	const sCtx = CanvasUtil.getShortvas(ctx);
	setupDiagramContext(
		sCtx,
		fontInfo,
		props,
		node.specialType,
		getCanvasSize,
		setCanvasSize
	);
	const errors = evalDiagramCode(sCtx, node, false);
	const blanks = sCtx.__diagramRc.blanks;
	delete sCtx.__diagramRc;
	return {ctx: sCtx, errors, blanks};
};

const evalDiagramCode = function(ctx, node, ignoreErrors) {
	const errors = [];
	ctx.save();
	ctx.setSize(100, 100);
	const colors = ColorUtil.getDiagramColors(); //eslint-disable-line no-unused-vars
	try {
		eval(node.code);
	} catch (err) {
		if (!ignoreErrors) {
			const reg = /at eval .*? <anonymous>:([0-9]*)/;
			const regResult = reg.exec(err.stack);
			const errLine = regResult ? regResult[1] : 0;
			let adjErrLine = errLine - node.preambleLines;
			if (adjErrLine < 0) {
				adjErrLine = "unknown";
			}
			if (err instanceof SyntaxError) {
				errors.push(
					"Diagram code contains a syntax error at line " +
						adjErrLine +
						": " +
						err.message
				);
			} else {
				errors.push(
					"Diagram code contains a javascript error at line " +
						adjErrLine +
						": " +
						err.message
				);
			}
		}
	}
	ctx.__callEndHooks();
	ctx.restore();
	return errors;
};

// Must pass a shortvas context.
const setupDiagramContext = function(
	ctx,
	fontInfo,
	props,
	specialType,
	getCanvasSize,
	setCanvasSize
) {
	let scaleFontSize = 20;
	let scaleFactor = fontInfo.size / scaleFontSize;

	// Error detection for missing rulerFontSize is done above.
	if (specialType === "forRuler") {
		// This should be used for diagrams in instructions
		scaleFactor = props.rulerFontSize || 20;
		scaleFontSize = 20 / scaleFactor;
	} else if (specialType === "forFracRuler") {
		// This should be used for diagrams in instructions
		// But don't think any instructions use this, only "forRuler"
		scaleFactor = (props.rulerFontSize || 20) * 3;
		scaleFontSize = 20 / scaleFactor;
	} else if (specialType === "ruler") {
		// This should be used for diagrams in problems
		scaleFontSize = 1;
		scaleFactor = fontInfo.size;
	}

	ctx.setSize = function(width, height) {
		setCanvasSize(width * scaleFactor, height * scaleFactor);
		const diagramRc = new RC(ctx);
		ctx.__diagramRc = diagramRc;
		//Pass certain props down to things drawn in diagram using CanvasMath
		const __cmProps = {};
		//overrideBlankSize needs to be scaled by the scaleFactor
		if (props.overrideBlankSize) {
			__cmProps.overrideBlankSize = {
				w: props.overrideBlankSize.w / scaleFactor,
				h: props.overrideBlankSize.h / scaleFactor,
			};
		}
		//Other blank & debug props can be included as-is
		_.extend(
			__cmProps,
			_.pick(props, [
				"insetBlurSize",
				"largestBlankCharacter",
				"blanksContainOperators",
				"measureWithoutOperators",
				"debugBoxes",
			])
		);
		ctx.__cmProps = __cmProps;
		ctx.font = scaleFontSize + "px Roboto";
		ColorUtil.setDefaultScheme(ctx);
		ctx.lineWidth = ctx.measureText("|").width / 4;
		// T42255: Although we can't reproduce it, the symptoms of this bug suggest
		// that the above value is coming out far larger than it should in rare
		// circumstances. So we'll try to detect that and force a better one.
		if (ctx.lineWidth > 0.1 * scaleFontSize) {
			// Coefficient is empirical; Chrome, FF, and Safari agreed on it in OSX.
			ctx.lineWidth = 0.0609 * scaleFontSize;
		}
		ctx.scale(scaleFactor, scaleFactor);
	};
	ctx.getSize = function() {
		const rawSize = getCanvasSize();
		return {
			//Returns the non-scaled size of the canvas
			width: rawSize.width / scaleFactor,
			height: rawSize.height / scaleFactor,
		};
	};
	ctx.changeFontSize = function(newSize) {
		ctx.font = Font.setSize(ctx, newSize);
	};
	ctx.resetDefaults = function() {
		ctx.font = scaleFontSize + "px Roboto";
		ColorUtil.setDefaultScheme(ctx);
		ctx.lineWidth = ctx.measureText("|").width / 4;
	};
	ctx.resetFont = function() {
		ctx.font = scaleFontSize + "px Roboto";
	};
	ctx.resetFillStyle = function() {
		ColorUtil.setDefaultFillStyle(ctx);
	};
	ctx.resetStrokeStyle = function() {
		ColorUtil.setDefaultStrokeStyle(ctx);
	};
	ctx.resetLineWidth = function() {
		ctx.save();
		ctx.font = scaleFontSize + "px Roboto";
		const lineWidth = ctx.measureText("|").width / 4;
		ctx.restore();
		ctx.lineWidth = lineWidth;
	};

	// Interface intended for DiagramHelpers only. Used by ShapeVolume to do
	// drawing after all 3D things are specified.
	const endHooks = [];
	ctx.__addEndHook = function(f) {
		endHooks.push(f);
	};
	ctx.__callEndHooks = function() {
		endHooks.forEach(f => f());
	};

	return ctx;
};

// Sanitize javascript code in diagrams.
const cleanDiagramCode = function(code) {
	let preambleLines = 0;
	//Change all " to ' or code doesn't work.
	code = code.replace(/"/g, "'");
	// Add in diagram helpers. Track how many lines for error purposes.
	for (const k in diagramHelpers) {
		preambleLines += 1;
		code = "var " + k + " = diagramHelpers." + k + ";\n" + code;
	}
	// Add "use strict"
	preambleLines += 1;
	code = '"use strict";\n' + code;
	return {code, preambleLines};
};

const makeCacheKey = function(code, specialType, fontInfo, props, dpr) {
	const obs = props.overrideBlankSize;
	const w = obs ? Math.floor(2 * obs.w) : "null";
	const h = obs ? Math.floor(2 * obs.h) : "null";
	const prefix =
		Math.floor(2 * fontInfo.size) +
		"|" +
		specialType +
		"|" +
		w +
		"|" +
		h +
		"|" +
		dpr +
		"\n";
	return prefix + code;
};

module.exports = {
	parseDiagram,
	drawDiagram,
	drawDiagramForTests,
	injectDependencies,
};
